import { AlertTriangle, CircleCheck, CircleHelp, OctagonAlert } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export type RagStatus = 'red' | 'amber' | 'green' | 'unknown';

/**
 * Hiển thị chuẩn cho tình trạng RAG. Mọi chỗ nói "đỏ / vàng / xanh" đều phải
 * đi qua đây thay vì tự tô màu.
 *
 * ⚠️ Icon **không** phải trang trí. Màu không được là kênh thông tin duy nhất
 * (docs/frontend-guide.md §4): khoảng 8% nam giới không phân biệt được đỏ với
 * xanh lá, và với họ một dashboard chỉ dùng màu là một dashboard trống. Đó
 * cũng là lý do có nhãn chữ, không chỉ chấm tròn.
 */
const CONFIG: Record<RagStatus, { icon: typeof CircleCheck; className: string }> = {
  red: { icon: OctagonAlert, className: 'text-rag-red border-rag-red/40 bg-rag-red/10' },
  amber: { icon: AlertTriangle, className: 'text-rag-amber border-rag-amber/40 bg-rag-amber/10' },
  green: { icon: CircleCheck, className: 'text-rag-green border-rag-green/40 bg-rag-green/10' },
  unknown: {
    icon: CircleHelp,
    className: 'text-rag-unknown border-rag-unknown/40 bg-rag-unknown/10',
  },
};

/** Icon của một mức RAG — cho chỗ tự vẽ khối màu nhưng vẫn phải có icon đi kèm. */
export function ragIconOf(status: RagStatus): typeof CircleCheck {
  return CONFIG[status].icon;
}

export function RagBadge({
  status,
  label,
  className,
}: {
  status: RagStatus;
  /** Ghi đè nhãn mặc định, vd "3 rủi ro đang mở" thay vì "Nguy cơ cao". */
  label?: ReactNode;
  className?: string;
}): ReactNode {
  const { t } = useTranslation();
  const { icon: Icon, className: tone } = CONFIG[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        tone,
        className,
      )}
      data-rag={status}
    >
      <Icon aria-hidden className="size-3.5" />
      {label ?? t(`rag.${status}`)}
    </span>
  );
}
